import utils from '../../../utils';
import React, { useState, useEffect } from 'react';
import YouTube from 'react-youtube';
import axios from 'axios';
import cmsUtils from "../../utils/cmsUtils";
import './style.scss';

const API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY || 'AIzaSyAtuNCYY5OdcjDEVWuQZFkqdEiwUecEuGM';

function getYoutubePlaylistId(value) {
    if (!value || typeof value !== 'string') {
        return '';
    }

    const trimmed = value.trim();
    if (!trimmed) {
        return '';
    }

    const listMatch = trimmed.match(/[?&]list=([a-zA-Z0-9_-]+)/);
    if (listMatch) {
        return listMatch[1];
    }

    if (/^PL[a-zA-Z0-9_-]+$/i.test(trimmed)) {
        return trimmed;
    }

    return trimmed.split(/[^a-zA-Z0-9_-]/)[0];
}

function mapPlaylistItems(items) {
    if (!items || !items.length) {
        return [];
    }

    return items
        .map((entry) => {
            const videoId = entry?.snippet?.resourceId?.videoId;
            if (!videoId || entry.snippet.title === 'Private video' || entry.snippet.title === 'Deleted video') {
                return null;
            }
            return {
                id: videoId,
                title: entry.snippet.title,
                thumbnail: entry.snippet.thumbnails?.medium?.url || entry.snippet.thumbnails?.default?.url,
            };
        })
        .filter(Boolean);
}

export function YouTubeWithPlaylist(props) {
    const item = props.item;
    const cmsOption = props.cmsOption || {};
    const videoCode = cmsUtils.payload(item, "YoutubeCode");

    const videosPerPage = 4;
    const [videoList, setVideoList] = useState([]);
    const [currentVideoId, setCurrentVideoId] = useState('');
    const [startIndex, setStartIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const playlistId = getYoutubePlaylistId(videoCode);

    const cssClass = utils.classNames(
        "cms_item",
        "youtubePanel",
        "youtube-with-playlist",
        "youtube-container",
        item.cssClass || item.anchorName || ""
    );

    useEffect(() => {
        if (!playlistId) {
            setVideoList([]);
            setCurrentVideoId('');
            setError('Please enter a YouTube playlist URL or playlist ID (e.g. PLIQLu30BHT5SB1YaF2qWnhLJ-ShD2xOOb).');
            return;
        }

        let cancelled = false;

        const fetchAllVideos = async () => {
            setLoading(true);
            setError('');

            try {
                let allVideos = [];
                let nextPageToken = '';

                do {
                    const response = await axios.get('https://www.googleapis.com/youtube/v3/playlistItems', {
                        params: {
                            part: 'snippet',
                            maxResults: 50,
                            playlistId,
                            key: API_KEY,
                            pageToken: nextPageToken || undefined,
                        },
                    });

                    const items = response.data?.items;
                    if (!items) {
                        throw new Error(response.data?.error?.message || 'Playlist could not be loaded.');
                    }

                    allVideos = [...allVideos, ...mapPlaylistItems(items)];
                    nextPageToken = response.data.nextPageToken || '';
                } while (nextPageToken && !cancelled);

                if (cancelled) {
                    return;
                }

                if (!allVideos.length) {
                    setVideoList([]);
                    setCurrentVideoId('');
                    setError('No videos found in this playlist.');
                    return;
                }

                setVideoList(allVideos);
                setCurrentVideoId(allVideos[0].id);
                setStartIndex(0);
            } catch (err) {
                if (cancelled) {
                    return;
                }
                console.error('Error fetching playlist:', err);
                setVideoList([]);
                setCurrentVideoId('');
                const apiMessage = err?.response?.data?.error?.message;
                setError(apiMessage || err.message || 'Failed to load playlist.');
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchAllVideos();

        return () => {
            cancelled = true;
        };
    }, [playlistId]);

    const opts = {
        height: '100%',
        width: '100%',
        playerVars: {
            autoplay: 1,
            mute: 1,
        },
    };

    const handlePrev = () => {
        setStartIndex(prev => Math.max(prev - videosPerPage, 0));
    };

    const handleNext = () => {
        setStartIndex(prev => Math.min(prev + videosPerPage, Math.max(videoList.length - videosPerPage, 0)));
    };

    const offset = videoList.length > 0
        ? `-${(startIndex / videosPerPage) * 100}%`
        : '0%';

    return (
        <div className={cssClass} data-cms-item-id={cmsOption.isCmsEdit && item.itemId} id={item.anchorName}>
            {loading && <p className="youtube-with-playlist__status">Loading playlist…</p>}
            {error && <p className="youtube-with-playlist__status youtube-with-playlist__status--error">{error}</p>}

            {currentVideoId && (
                <YouTube videoId={currentVideoId} opts={opts} className='main-video-container' />
            )}

            {videoList.length > 0 && (
                <>
                    <div className="playlist-container">
                        <div className="video-thumbnails" style={{ transform: `translateX(${offset}%)` }}>
                            {videoList.slice(startIndex, startIndex + videosPerPage).map(video => (
                                <div key={`${video.id}-${video.title}`} className="thumbnail" onClick={() => setCurrentVideoId(video.id)}>
                                    <img src={video.thumbnail} alt={video.title} />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className='arrow-container'>
                        <button className="arrow left" onClick={handlePrev} disabled={startIndex === 0}>← Prev</button>
                        <button className="arrow right" onClick={handleNext} disabled={startIndex + videosPerPage >= videoList.length}>Next →</button>
                    </div>
                </>
            )}
        </div>
    );
}
